import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Activity, MessageSquare, Mail, CheckCircle, XCircle, Trash2, LogOut, X, BookOpen, Plus, Zap, Edit } from 'lucide-react';

const API_URL = window.location.origin;

export default function AdminPanel({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userConversations, setUserConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [corrections, setCorrections] = useState([]);
  const [loadingCorrections, setLoadingCorrections] = useState(false);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [correctionForm, setCorrectionForm] = useState({
    correct_answer: '',
    note: ''
  });

  useEffect(() => {
    fetchAdminData();
    if (activeTab === 'training') {
      fetchCorrections();
    }
  }, [activeTab]);

  const fetchAdminData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsResponse, usersResponse] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/users`, { headers })
      ]);

      setStats(statsResponse.data);
      setUsers(usersResponse.data.users);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load admin data');
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCorrections = async () => {
    setLoadingCorrections(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/admin/corrections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorrections(response.data.corrections);
    } catch (err) {
      alert('Failed to load corrections');
    } finally {
      setLoadingCorrections(false);
    }
  };

  const fetchUserConversations = async (userId, username) => {
    setSelectedUser({ id: userId, username });
    setLoadingConversations(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserConversations(response.data.conversations);
    } catch (err) {
      alert('Failed to load conversations');
    } finally {
      setLoadingConversations(false);
    }
  };

  const handleVerifyUser = async (userId, username) => {
    if (!window.confirm(`Verify user "${username}"?`)) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API_URL}/api/admin/verify-user/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('User verified successfully!');
      await fetchAdminData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to verify user');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`)) return;

    setDeleteLoading(userId);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchAdminData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteLoading(null);
    }
  };

  const openCorrectionModal = (conversation) => {
    setSelectedConversation(conversation);
    setCorrectionForm({ correct_answer: '', note: '' });
    setShowCorrectionModal(true);
  };

  const handleAddCorrection = async () => {
    if (!correctionForm.correct_answer.trim()) {
      alert('Please enter the correct answer');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API_URL}/api/admin/add-correction`, {
        conversation_id: selectedConversation.id,
        question: selectedConversation.question,
        wrong_answer: selectedConversation.answer,
        correct_answer: correctionForm.correct_answer,
        note: correctionForm.note
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Correction added successfully!');
      setShowCorrectionModal(false);
      setSelectedConversation(null);
      setCorrectionForm({ correct_answer: '', note: '' });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add correction');
    }
  };

  const handleDeleteCorrection = async (index) => {
    if (!window.confirm('Delete this correction?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/api/admin/corrections/${index}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCorrections();
    } catch (err) {
      alert('Failed to delete correction');
    }
  };

  const handleTrainModel = async () => {
    if (!window.confirm(`Train model with ${corrections.length} corrections? This will take 5-10 minutes.`)) return;

    setTrainingLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API_URL}/api/admin/train-model`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(`✅ Model trained successfully!\n${response.data.corrections_applied} corrections applied.`);
      await fetchCorrections();
    } catch (err) {
      alert(err.response?.data?.detail || 'Training failed');
    } finally {
      setTrainingLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    onLogout();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-cyan-600 mx-auto" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img src="/logo.png" alt="TwoRun.AI Logo" className="w-12 h-12 object-contain" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">TwoRun.AI Admin</h1>
                <p className="text-sm text-gray-500">Welcome, {localStorage.getItem('adminUsername')}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="w-5 h-5 inline mr-2" />
              Users & Conversations
            </button>
            <button
              onClick={() => setActiveTab('training')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'training'
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BookOpen className="w-5 h-5 inline mr-2" />
              AI Training ({corrections.length})
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg">
            <p className="font-semibold flex items-center">
              <span className="mr-2">⚠️</span> Error
            </p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        {/* Stats Grid */}
        {activeTab === 'users' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow p-6 border-l-4 border-cyan-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Total Users</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total_users}</p>
                </div>
                <div className="bg-cyan-100 p-3 rounded-full">
                  <Users className="w-8 h-8 text-cyan-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Verified Users</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.verified_users}</p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow p-6 border-l-4 border-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Total Chats</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total_chats}</p>
                </div>
                <div className="bg-orange-100 p-3 rounded-full">
                  <Activity className="w-8 h-8 text-orange-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow p-6 border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Total Messages</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total_conversations}</p>
                </div>
                <div className="bg-purple-100 p-3 rounded-full">
                  <MessageSquare className="w-8 h-8 text-purple-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Registered Users</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registered</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                        No users registered yet
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{user.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.username}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 mr-2 text-gray-400" />
                            {user.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.is_verified ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <XCircle className="w-3 h-3 mr-1" />
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-3">
                            {!user.is_verified && (
                              <button
                                onClick={() => handleVerifyUser(user.id, user.username)}
                                className="text-green-600 hover:text-green-900 transition"
                                title="Verify user"
                              >
                                <CheckCircle className="w-5 h-5" />
                              </button>
                            )}
                            <button
                              onClick={() => fetchUserConversations(user.id, user.username)}
                              className="text-cyan-600 hover:text-cyan-900 transition"
                              title="View conversations"
                            >
                              <MessageSquare className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id, user.username)}
                              disabled={deleteLoading === user.id}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              title="Delete user"
                            >
                              {deleteLoading === user.id ? (
                                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <Trash2 className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Training Tab */}
        {activeTab === 'training' && (
          <div>
            <div className="bg-white rounded-xl shadow overflow-hidden mb-6">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Pending Corrections</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {corrections.length} correction{corrections.length !== 1 ? 's' : ''} ready to train
                  </p>
                </div>
                {corrections.length > 0 && (
                  <button
                    onClick={handleTrainModel}
                    disabled={trainingLoading}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold"
                  >
                    {trainingLoading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Training Model...</span>
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5" />
                        <span>Train Model</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              <div className="p-6">
                {loadingCorrections ? (
                  <div className="flex justify-center py-12">
                    <svg className="animate-spin h-8 w-8 text-cyan-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : corrections.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">No corrections yet</p>
                    <p className="text-gray-400 text-sm mt-2">Add corrections from user conversations to train the model</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {corrections.map((corr, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-cyan-500 transition">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <p className="text-xs text-gray-500">Added by {corr.added_by} • {formatDate(corr.added_at)}</p>
                          </div>
                          <button
                            onClick={() => handleDeleteCorrection(index)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete correction"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-1">Question:</p>
                            <p className="text-sm text-gray-900">{corr.question}</p>
                          </div>

                          <div className="bg-red-50 p-3 rounded">
                            <p className="text-xs font-semibold text-red-600 mb-1">❌ Wrong Answer:</p>
                            <p className="text-sm text-gray-900">{corr.wrong_answer}</p>
                          </div>

                          <div className="bg-green-50 p-3 rounded">
                            <p className="text-xs font-semibold text-green-600 mb-1">✅ Correct Answer:</p>
                            <p className="text-sm text-gray-900">{corr.correct_answer}</p>
                          </div>

                          {corr.note && (
                            <div className="bg-blue-50 p-3 rounded">
                              <p className="text-xs font-semibold text-blue-600 mb-1">📝 Note:</p>
                              <p className="text-sm text-gray-900">{corr.note}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Conversations Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[80vh] flex flex-col" style={{ maxWidth: '1280px' }}>
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Conversations - {selectedUser.username}</h2>
                <p className="text-sm text-gray-500 mt-1">User ID: #{selectedUser.id}</p>
              </div>
              <button
                onClick={() => setSelectedUser(null)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {loadingConversations ? (
                <div className="flex justify-center items-center h-64">
                  <svg className="animate-spin h-8 w-8 text-cyan-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : userConversations.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No conversations yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {userConversations.map((conv) => (
                    <div key={conv.id} className="border-l-4 border-cyan-500 bg-gray-50 rounded-r-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs text-gray-500">#{conv.id}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">{formatDate(conv.created_at)}</span>
                          <button
                            onClick={() => openCorrectionModal(conv)}
                            className="text-orange-600 hover:text-orange-800 transition"
                            title="Add correction"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <div className="flex items-start space-x-2">
                          <div className="bg-cyan-100 rounded-full p-2 mt-1">
                            <Users className="w-4 h-4 text-cyan-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-600 mb-1">Question:</p>
                            <p className="text-sm text-gray-900">{conv.question}</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-start space-x-2">
                          <div className="bg-purple-100 rounded-full p-2 mt-1">
                            <MessageSquare className="w-4 h-4 text-purple-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-600 mb-1">Answer:</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{conv.answer}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setSelectedUser(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Correction Modal */}
      {showCorrectionModal && selectedConversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Add Training Correction</h2>
              <button
                onClick={() => setShowCorrectionModal(false)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Question:</label>
                <div className="bg-gray-50 p-3 rounded border border-gray-200">
                  <p className="text-sm text-gray-900">{selectedConversation.question}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-red-600 mb-2">❌ Current (Wrong) Answer:</label>
                <div className="bg-red-50 p-3 rounded border border-red-200">
                  <p className="text-sm text-gray-900">{selectedConversation.answer}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-green-600 mb-2">✅ Correct Answer: *</label>
                <textarea
                  value={correctionForm.correct_answer}
                  onChange={(e) => setCorrectionForm({ ...correctionForm, correct_answer: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  rows="6"
                  placeholder="Enter the correct answer here..."
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">📝 Note (optional):</label>
                <textarea
                  value={correctionForm.note}
                  onChange={(e) => setCorrectionForm({ ...correctionForm, note: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  rows="3"
                  placeholder="Add any notes or context..."
                ></textarea>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowCorrectionModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCorrection}
                className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition font-semibold"
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Add Correction
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}